import { FilePlus2, Trash2 } from "lucide-react";
import { Link } from "@/lib/nav";
import { useState } from "react";
import { toast } from "sonner";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { deleteProjectLog } from "@/api/actions/project-log";
import type { LogCommentItem } from "@buildpy/core/modules/project/log-comment";
import { PROJECT_LOG_KIND_LABEL, type ProjectLogKind } from "@buildpy/core/modules/project/types";
import LogComments from "@/modules/project/ui/log-comments";
import { LOG_KIND_TONE } from "@/modules/project/ui/log-kind-tone";
import { useRecordAction } from "@/modules/project/ui/use-record-action";
import { photoViewKeys } from "@/modules/project/ui/use-photo-upload";
import { dropDetachedViews } from "@/lib/query";

export interface LogListItem {
  id: string;
  kind: ProjectLogKind;
  body: string;
  // 작성자 · 시각 한 줄.
  meta: string;
  canDelete: boolean;
  // 추가공정·노쇼 줄의 「변경공사로」 링크(만들 수 있을 때만).
  changeHref?: string | null;
  weather?: string | null;
  // 그 일지의 댓글 — 없으면(undefined) 댓글 줄을 그리지 않는다.
  comments?: readonly LogCommentItem[];
}

interface Props {
  projectId: string;
  logs: readonly LogListItem[];
}

// 그날 현장일지 — 쓴 순서대로. 지우기는 작성자·대표만 보인다.
const LogList = ({ projectId, logs }: Props) => {
  const { pending, run } = useRecordAction();
  // 지우기 확인을 기다리는 일지.
  const [askId, setAskId] = useState<string | null>(null);

  const remove = (logId: string) => {
    void run("일지를 지우지 못했습니다", () => deleteProjectLog(projectId, logId), () => {
      dropDetachedViews(photoViewKeys(projectId));
      toast.success("일지를 지웠습니다");
    });
  };

  if (logs.length === 0) {
    return <EmptyState>아직 쓴 일지가 없습니다.</EmptyState>;
  }

  return (
    <ul className="flex flex-col gap-y-2" data-testid="today-log-list">
      {logs.map((log) => (
        <SectionCard key={log.id} as="li" className="text-sm" padding="sm">
          <div className="flex items-start justify-between gap-x-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2">
              <Pill tone={LOG_KIND_TONE[log.kind]}>{PROJECT_LOG_KIND_LABEL[log.kind]}</Pill>
              {log.weather && (
                <span className="text-xs text-muted-foreground" data-testid="log-weather-text">
                  날씨 {log.weather}
                </span>
              )}
            </div>
            {log.canDelete && (
              <Button
                aria-label="일지 지우기"
                className="-m-1"
                disabled={pending}
                size="icon-xs"
                type="button"
                variant="ghost-destructive"
                onClick={() => setAskId(log.id)}>
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
          <p className="mt-1.5 whitespace-pre-wrap break-words">{log.body}</p>
          <div className="mt-1 flex items-center justify-between gap-x-2">
            <p className="text-xs text-muted-foreground">{log.meta}</p>
            {log.changeHref && (
              <Link
                className="inline-flex shrink-0 items-center gap-x-1 text-xs font-medium text-primary hover:underline"
                data-testid="log-change-order-link"
                href={log.changeHref}>
                <FilePlus2 className="size-3.5" />
                변경공사로
              </Link>
            )}
          </div>
          {log.comments && <LogComments comments={log.comments} logId={log.id} projectId={projectId} />}
        </SectionCard>
      ))}
      <ConfirmDialog
        destructive
        confirmLabel="지우기"
        description="지운 일지는 되돌릴 수 없습니다."
        open={askId !== null}
        testId="log-delete-confirm"
        title="이 일지를 지울까요?"
        onConfirm={() => askId && remove(askId)}
        onOpenChange={(open) => !open && setAskId(null)}
      />
    </ul>
  );
};

export default LogList;
